// Pure helpers for the Phase 3 fork-update flow (issue #3198). Lives outside Angular DI
// so the test runner can import without bootstrapping the framework. Production code in
// coach-exercise-sync.service.ts, exercise-db.service.ts, and edit-exercise-page.ts all
// import from here, so tests exercise the real shipped logic — not re-implementations.

import { CustomExercise, isForkUde, isOwnedCustomExercise } from '@cf-shared/data-model/exercise';
import { isPouchNotFound } from '@cf-shared/data-model/pouch';
import { copyDiffField, EXERCISE_DIFF_FIELDS, ExerciseDiffField } from './exercise-diff';
import { isTransientFetchError } from './error-patterns';
import { isDatabaseRecovering } from './database-unavailable-error';

// Minimal user-DB shape the own-DB source resolver needs. Kept structural so this helper
// stays free of Angular DI and is unit-testable. `getCustomExerciseByIdAsync` THROWS
// `isPouchNotFound` on a missing doc (it does NOT return undefined).
export interface OwnCustomExerciseLookup {
  getCustomExerciseByIdAsync(id: string): Promise<CustomExercise>;
}

// Resolve a custom-exercise source from the user's OWN database, returning undefined when the
// id isn't present locally. Used by `checkImportedExerciseForUpdateAsync` so a same-user local
// fork source (issue #3808 — a `+UDE-` doc that lives only in this user's DB, never published
// to the shared `global_exercises` staging DB or the server) is found instead of being wrongly
// reported as a deleted shared exercise. Mirrors the own-DB-first lookup the edit page already
// does for its "Based on: <name>" line (`resolveSourceExerciseNameAsync`).
export async function resolveLocalCustomExerciseAsync(
  userDb: OwnCustomExerciseLookup,
  id: string,
): Promise<CustomExercise | undefined> {
  try {
    return await userDb.getCustomExerciseByIdAsync(id);
  } catch (err) {
    if (isPouchNotFound(err)) return undefined;
    throw err;
  }
}

// R1 part 1 — records the staging-DB `_rev` that this userdb copy was last applied from.
// Without this, a fork created off the coach copy would inherit a userdb-local rev as its
// `local.sourceRevision` baseline and the banner would fire spuriously forever (the rev
// spaces are disjoint). Called from both branches (first arrival, existing copy) of
// planCoachDocWrite in coach-exercise-sync-gates.ts, which builds the one write that both
// applyCoachDocAsync and its 409 retry go through.
export function stampCoachChannelRevision(target: CustomExercise, stagingRev: string): void {
  target.local = target.local ?? {};
  target.local.coachChannelRevision = stagingRev;
}

// R1 part 2 — true for a coach userdb copy (`coachShared` is preserved by
// `stripFieldsForPublish` so it's the reliable marker). A fork of a coach copy has
// `local.coachShared` undefined because `forkAndSaveAsync` does a full
// `fork.local = { sourceExerciseId }` overwrite. Gates the banner off coach copies
// inside `checkImportedExerciseForUpdateAsync`.
export function isCoachCopy(local: CustomExercise['local']): boolean {
  return !!local?.coachShared;
}

// R3 — the banner-fire condition. True when source rev is newer AND the user hasn't
// already reviewed this exact rev via the picker. A genuinely-new coach edit advances
// `sourceRev` past `lastSeenSourceRevision`, so the banner re-appears.
export function computeHasUpdate(local: CustomExercise['local'], sourceRev: string): boolean {
  if (!local) return false;
  const hasNewerRev = sourceRev !== local.sourceRevision;
  const alreadySeenThisRev = sourceRev === local.lastSeenSourceRevision;
  return hasNewerRev && !alreadySeenThisRev;
}

// R1 part 3 — fork-path lineage-rev selection. Prefers the source's stamped staging rev
// so a fork's `local.sourceRevision` is always a real staging-DB rev (same sequence as
// `checkImportedExerciseForUpdateAsync`'s comparison target) — never a userdb-local rev.
//   - coach-applied copy → `coachChannelRevision` (stamped by R1 part 1)
//   - public-share import → `sourceRevision` (the import flow already populated it with
//     the true remote rev)
//   - plain custom exercise → `_rev` fallback
// Coach-fork and public-share-fork paths thus converge through one chain.
export function pickForkSourceRev(initial: CustomExercise): string | undefined {
  return initial.local?.coachChannelRevision ?? initial.local?.sourceRevision ?? initial._rev;
}

// 3-way-diff base snapshot: pick the diffable field values off a source doc (deep-clone
// `standard` so the snapshot never aliases the live doc). Stored on the fork as
// `local.coachBaseFields` and advanced ONLY on a full apply (partial apply / ignore leave
// it — see `applyDiffFieldsToCanonical`) so the update picker can tell
// coach-changes-since-base apart from the user's own divergences.
export function snapshotDiffFields(source: CustomExercise): NonNullable<CustomExercise['local']>['coachBaseFields'] {
  const snapshot: NonNullable<NonNullable<CustomExercise['local']>['coachBaseFields']> = {};
  for (const field of EXERCISE_DIFF_FIELDS) copyDiffField(snapshot, source, field);
  return snapshot;
}

// R3 — single source of apply truth, shared by the edit-exercise-page draft flow and the
// in-place exercise-switch flow. Mutates `target` in place: copies each picked field from
// `source` (deep-cloning `standard` so the userdb copy never aliases the staging doc's
// nested object), then stamps the R3 apply-table revs — `lastSeenSourceRevision` always
// advances; `sourceRevision` advances ONLY on a full apply (the fork genuinely
// re-derived). For an ignore, call with an empty `appliedFields` and `isFullApply=false`
// to stamp `lastSeenSourceRevision` only. Never touches `sourceExerciseId` /
// `coachShared` / identity fields.
//
// `coachBaseFields` (the 3-way-diff base) advances ONLY on a full apply. On partial
// apply / ignore the base is intentionally LEFT at the prior point so a later
// "Check for Update" re-shows the coach changes the user hasn't fully incorporated
// (instead of an empty "already up to date" diff). `lastSeenSourceRevision` still moves
// on every resolution, so the auto-banner stays suppressed until a genuinely-new rev.
export function applyDiffFieldsToCanonical(
  target: CustomExercise,
  source: CustomExercise,
  appliedFields: Set<ExerciseDiffField>,
  sourceRev: string,
  isFullApply: boolean,
): void {
  for (const field of appliedFields) {
    // if a caller hands in a field that is not on the list (an untyped value), fail loudly
    // rather than silently applying nothing for it
    if (!EXERCISE_DIFF_FIELDS.includes(field)) {
      throw new Error('applyDiffFieldsToCanonical: unknown ExerciseDiffField ' + String(field));
    }
    copyDiffField(target, source, field);
  }
  target.local = target.local ?? {};
  target.local.lastSeenSourceRevision = sourceRev;
  if (isFullApply) {
    target.local.sourceRevision = sourceRev;
    // fork fully re-derived from this rev — advance the base so future updates diff
    // against it. Partial apply / ignore deliberately leave the base unchanged.
    target.local.coachBaseFields = snapshotDiffFields(source);
  }
}

// ---------------------------------------------------------------------------------------
// Issue #3808 — heal for spurious self-forks.
//
// Background: before the ownership fix, editing the user's OWN legacy exercise (a `+UDE-`
// doc with no `hashSource`) was wrongly treated as editing a foreign exercise and forked a
// duplicate. The result is a fork F (the edited copy, referenced by the plan and carrying the
// live progression) whose `local.sourceExerciseId` points at the now-stale base S in the same
// user's DB. F then shows the false "original shared exercise is no longer available" banner.
//
// The heal collapses each such pair: de-fork F IN PLACE (so F keeps its id, plan references,
// and progression — only the spurious lineage is removed), and soft-delete the orphan base S
// when nothing else references it. We deliberately do NOT re-point the plan from F to S:
// `replaceExerciseInSubSlot` (the only sanctioned way to change `subSlot.exercise_id`) wipes
// the progression path state, which would throw away the user's training history for that
// exercise.

export interface SpuriousSelfForkHealPlan {
  // forks to de-fork in place (strip the spurious upstream lineage)
  forkIdsToDefork: string[];
  // orphan base docs to soft-delete (provably unreferenced once the forks are de-forked)
  baseIdsToSoftDelete: string[];
}

// De-fork a spuriously-created self-fork IN PLACE: drop the upstream lineage so the doc
// becomes a plain owned exercise (no banner, no update-check), keeping its id, edited fields,
// and `hashSource`. Idempotent — once `sourceExerciseId` is gone the doc is no longer a fork.
export function deforkSelfFork(fork: CustomExercise): void {
  if (!fork.local) return;
  delete fork.local.sourceExerciseId;
  delete fork.local.sourceRevision;
  delete fork.local.coachBaseFields;
  delete fork.local.lastSeenSourceRevision;
}

// Why a self-fork heal run that failed may simply run again later (df-622fe503ca9b).
export enum SelfForkHealDeferReason {
  // the read went to the server before the first sync settled, and the network dropped
  Network = 'network',
  // a database recovery cut the read short; the same read works again shortly
  Recovering = 'recovering',
}

// Classify a failed self-fork heal run. Returns a reason when the failure is an expected,
// self-healing condition, so the caller defers the heal and reports at the info tier.
// Returns undefined for anything else, which stays an unresolved problem.
export function selfForkHealDeferReason(err: unknown): SelfForkHealDeferReason | undefined {
  if (isTransientFetchError(err)) return SelfForkHealDeferReason.Network;
  if (isDatabaseRecovering(err)) return SelfForkHealDeferReason.Recovering;
  return undefined;
}

// Decide which of the user's custom exercises are spurious self-forks to collapse (#3808).
// A fork F qualifies when:
//   - F is the user's OWN fork (`isOwnedCustomExercise` AND `isForkUde`), and
//   - F's source S is present in the user's OWN customs, is itself the user's OWN exercise,
//     is NOT a fork (we only collapse one hop onto a base), and is NOT already soft-deleted.
// A genuine shared/imported fork has a source authored elsewhere that is NOT in the own DB (or
// is not owned), so it never matches and is left untouched.
//
// A base S is added to `baseIdsToSoftDelete` only when, after de-forking, it is referenced by
// nothing: not used directly by the plan (`isReferencedByPlan`) and no remaining live UDE points
// at it via `sourceExerciseId`. This is pure and idempotent: a second run over already-healed
// data finds no forks (their `sourceExerciseId` is gone) and returns empty lists.
export function planSpuriousSelfForkHeal(
  customs: CustomExercise[],
  uid: string | undefined,
  isReferencedByPlan: (exerciseId: string) => boolean,
): SpuriousSelfForkHealPlan {
  const byId = new Map(customs.map(e => [e._id, e]));
  const forkIdsToDefork: string[] = [];
  const deforkedSet = new Set<string>();
  const candidateBaseIds = new Set<string>();
  for (const ex of customs) {
    if (ex.virtuallyDeleted) continue;
    if (!isForkUde(ex)) continue;
    if (!isOwnedCustomExercise(ex, uid)) continue; // F must be the user's own fork
    const srcId = ex.local!.sourceExerciseId!;
    const src = byId.get(srcId);
    if (!src) continue;                             // source not in own DB → genuine foreign fork
    if (src.virtuallyDeleted) continue;             // source already retired → leave for lineage walk
    if (isForkUde(src)) continue;                   // only collapse a one-hop self-fork onto a base
    if (!isOwnedCustomExercise(src, uid)) continue; // source must be the user's OWN exercise
    forkIdsToDefork.push(ex._id);
    deforkedSet.add(ex._id);
    candidateBaseIds.add(srcId);
  }
  const baseIdsToSoftDelete: string[] = [];
  for (const baseId of candidateBaseIds) {
    if (isReferencedByPlan(baseId)) continue;
    // after de-forking, does any live custom still reference this base?
    const stillReferenced = customs.some(e => {
      if (e.virtuallyDeleted || e._id === baseId) return false;
      // another live custom uses S as its "Most Similar Exercise" muscle-targeting reference
      if (e.similarExerciseId === baseId) return true;
      // a live fork we are NOT de-forking still points at S as its source
      if (!deforkedSet.has(e._id) && e.local?.sourceExerciseId === baseId) return true;
      return false;
    });
    if (!stillReferenced) baseIdsToSoftDelete.push(baseId);
  }
  return { forkIdsToDefork, baseIdsToSoftDelete };
}
